import React, { FC, useCallback, useContext, useEffect } from 'react';
import classNames from 'classnames';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import XIcon from 'icons/X.icon';
import { Button } from 'ncoded-component-library';
import AuthContext from 'providers/Auth/Auth.context';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import {
  isEnterPressed,
  isOpenDrawerOrModal,
  scrollToErrorsOnSubmit,
} from 'utils';

import './FormWrapper.styles.scss';

type FormWrapperProps = {
  className?: string;
  title?: string;
  exitRoute?: string;
  disabledSubmit?: boolean;
  currentStep?: number;
  submitText?: string;
  hasFooter?: boolean;
  onBack?: () => void;
};

const ALLOWED_HTML_TAG_FOR_ENTER_SUBMIT = ['BODY', 'INPUT'];

const FormWrapper: FC<FormWrapperProps> = ({
  className,
  children,
  title,
  exitRoute,
  disabledSubmit,
  submitText,
  currentStep = 0,
  hasFooter = true,
  onBack,
}) => {
  const { navigateBack } = useContext(AuthContext);

  const { submit } = useForm();
  const { valid, invalid } = useFormState();

  const { t } = useTranslation();

  const classes = classNames('form-wrapper', { valid }, className);

  const onFinalEnterSubmit = useCallback(
    (e: KeyboardEvent) => {
      if (isOpenDrawerOrModal()) return;

      const { tagName } = document.activeElement;

      const targetClassLists = Array.from((e.target as Element).classList).join(
        ',',
      );

      const isSelect = targetClassLists.includes('ncoded-select');

      if (
        isEnterPressed(e) &&
        valid &&
        !isSelect &&
        ALLOWED_HTML_TAG_FOR_ENTER_SUBMIT.includes(tagName)
      ) {
        submit();
      }
    },
    [valid, submit],
  );

  useEffect(() => {
    document.addEventListener('keydown', onFinalEnterSubmit);

    return () => {
      document.removeEventListener('keydown', onFinalEnterSubmit);
    };
  }, [onFinalEnterSubmit]);

  return (
    <div className={classes}>
      <div className="form-wrapper__header">
        <div>
          <h1>{title}</h1>
          <Button
            variant="icon"
            aria-label="Close"
            icon={XIcon}
            onClick={() =>
              navigateBack({ replace: true, fallbackRoute: exitRoute })
            }
          />
        </div>
      </div>

      <div className="form-wrapper__content">
        <div>{children}</div>
      </div>

      {hasFooter && (
        <div className="form-wrapper__footer">
          <div>
            {currentStep !== 0 ? (
              <Button
                type="button"
                variant="link"
                className="back-button"
                icon={ArrowleftIcon}
                onClick={onBack}
              />
            ) : (
              <span />
            )}

            <Button
              disabled={disabledSubmit}
              icon={ArrowrightIcon}
              iconPosition="right"
              onClick={() => {
                scrollToErrorsOnSubmit(invalid, submit);
              }}
            >
              {submitText || t('next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormWrapper;
